import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { useAppAuth } from "@/context/AppAuthContext";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";

export default function Auth({ mode = "login" }) {
  const [tab, setTab] = useState(mode);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useAppAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/profil";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("pd_login_from", redirectTo);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast.success("Влязохте успешно.");
      } else {
        await register({ name: form.name, email: form.email, password: form.password, phone: form.phone });
        toast.success("Профилът е създаден.");
      }
      navigate(redirectTo);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClass = "mb-1.5 block text-sm font-semibold text-slate-700";

  return (
    <Layout showTicker={false}>
      <Seo title={tab === "login" ? "Вход" : "Регистрация"} noindex />
      <div className="mx-auto max-w-md px-4 py-14 sm:px-6" data-testid="account-auth">
        <div className="mb-6 grid grid-cols-2 gap-1 rounded-full bg-slate-100 p-1">
          <button
            onClick={() => setTab("login")}
            data-testid="auth-tab-login"
            className={`rounded-full py-2 text-sm font-bold transition-colors ${tab === "login" ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}
          >
            Вход
          </button>
          <button
            onClick={() => setTab("register")}
            data-testid="auth-tab-register"
            className={`rounded-full py-2 text-sm font-bold transition-colors ${tab === "register" ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}
          >
            Регистрация
          </button>
        </div>

        <h1 className="font-display text-2xl font-extrabold text-slate-900">
          {tab === "login" ? "Вход в профила" : "Създаване на профил"}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {tab === "login"
            ? "Влезте, за да виждате контакти и да публикувате обяви."
            : "Регистрирайте се безплатно, за да публикувате обяви и виждате контакти."}
        </p>

        <button
          type="button"
          onClick={googleLogin}
          data-testid="google-login-btn"
          className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
            <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1S8.7 5.9 12 5.9c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.3 14.6 2.3 12 2.3 6.9 2.3 2.8 6.4 2.8 11.5S6.9 20.7 12 20.7c5.2 0 8.6-3.6 8.6-8.7 0-.6-.06-1-.14-1.5H12z" />
          </svg>
          Продължи с Google
        </button>

        <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
          <span className="h-px flex-1 bg-slate-200" />
          или с имейл
          <span className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">          {tab === "register" && (
            <div>
              <label className={labelClass}>Име *</label>
              <input className={inputClass} data-testid="auth-name" value={form.name} onChange={set("name")} placeholder="Вашето име" />
            </div>
          )}
          <div>
            <label className={labelClass}>Имейл *</label>
            <input type="email" className={inputClass} data-testid="auth-email" value={form.email} onChange={set("email")} placeholder="you@email.bg" />
          </div>
          <div>
            <label className={labelClass}>Парола *</label>
            <input type="password" className={inputClass} data-testid="auth-password" value={form.password} onChange={set("password")} placeholder={tab === "register" ? "поне 6 символа" : "••••••••"} />
            {tab === "login" && (
              <div className="mt-1.5 text-right">
                <Link to="/zabravena-parola" data-testid="forgot-link" className="text-xs font-medium text-primary hover:underline">
                  Забравена парола?
                </Link>
              </div>
            )}
          </div>
          {tab === "register" && (
            <div>
              <label className={labelClass}>Телефон (по избор)</label>
              <input className={inputClass} data-testid="auth-phone" value={form.phone} onChange={set("phone")} placeholder="напр. 0888…" />
              <p className="mt-1 text-xs text-slate-400">Скоро ще добавим потвърждение на телефона за повече доверие.</p>
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0041A8] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : tab === "login" ? <LogIn className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
            {tab === "login" ? "Влез" : "Регистрирай се"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-slate-400">
          С регистрацията приемате нашите{" "}
          <Link to="/obshti-usloviya" className="text-primary hover:underline">Общи условия</Link> и{" "}
          <Link to="/poveritelnost" className="text-primary hover:underline">Политика за поверителност</Link>.
        </p>
      </div>
    </Layout>
  );
}
